import React from 'react';

import { ConfigMenu, ConfigMenuGroup } from '@reckon-web/config-menu';
import { SlidersIcon } from '@reckon-web/icon';
import { IconButton } from '@reckon-web/button';
import { Tooltip } from '@reckon-web/tooltip';
import { makeId } from '@reckon-web/utils';

import {
  VisibleProperties as VisiblePropertiesType,
  useVisibleProperties,
} from '../common/visible-properties';

import { JobsOrTasks } from './jobs-or-tasks';
import { KanbanOrList } from './kanban-or-list';

export type WorkType = JobsOrTasks | 'completedJobs';

const taskListOptions: {
  label: string;
  value: keyof VisiblePropertiesType['taskList'];
  disabled: boolean;
}[] = [
  { label: 'Debtor', value: 'debtor', disabled: true },
  { label: 'Job', value: 'job', disabled: true },
  { label: 'Task', value: 'task', disabled: true },
  { label: 'Client', value: 'client', disabled: false },
  { label: 'Assigned', value: 'assigned', disabled: false },
  { label: 'Status', value: 'status', disabled: false },
  { label: 'Due date', value: 'dueDate', disabled: false },
  { label: 'Estimated time', value: 'estimatedTime', disabled: false },
];

const taskBoardOptions: {
  label: string;
  value: keyof VisiblePropertiesType['taskBoard'];
  disabled: boolean;
}[] = [
  { label: 'Debtor', value: 'debtor', disabled: true },
  { label: 'Job', value: 'job', disabled: true },
  { label: 'Task', value: 'task', disabled: true },
  { label: 'Client', value: 'client', disabled: false },
  { label: 'Assigned', value: 'assigned', disabled: false },
  { label: 'Date', value: 'dueDate', disabled: false },
  { label: 'Estimated time', value: 'estimatedTime', disabled: false },
];

const jobListOptions: {
  label: string;
  value: keyof VisiblePropertiesType['jobList'];
  disabled: boolean;
}[] = [
  { label: 'Debtor', value: 'debtor', disabled: true },
  { label: 'Job', value: 'job', disabled: true },
  { label: 'Assignee', value: 'assignee', disabled: false },
  { label: 'Partner', value: 'partner', disabled: false },
  { label: 'Manager', value: 'manager', disabled: false },
  { label: 'Staff', value: 'staff', disabled: false },
  { label: 'Status', value: 'status', disabled: false },
  { label: 'Due Date', value: 'dueDate', disabled: false },
  { label: 'Budget', value: 'budget', disabled: false },
  { label: 'Tasks', value: 'tasks', disabled: false },
];

const jobBoardOptions: {
  label: string;
  value: keyof VisiblePropertiesType['jobBoard'];
  disabled: boolean;
}[] = [
  { label: 'Debtor', value: 'debtor', disabled: true },
  { label: 'Job', value: 'job', disabled: true },
  { label: 'Assignee', value: 'assignee', disabled: false },
  { label: 'Staff', value: 'staff', disabled: false },
  { label: 'Partner', value: 'partner', disabled: false },
  { label: 'Manager', value: 'manager', disabled: false },
  { label: 'Date', value: 'dueDate', disabled: false },
  { label: 'Tasks', value: 'tasks', disabled: false },
];

const completedJobsTableOptions: {
  label: string;
  value: keyof VisiblePropertiesType['completedJobTable'];
  disabled: boolean;
}[] = [
  { label: 'Assignee', value: 'assignee', disabled: false },
  { label: 'Partner', value: 'partner', disabled: false },
  { label: 'Manager', value: 'manager', disabled: false },
  { label: 'Staff', value: 'staff', disabled: false },
  { label: 'Job', value: 'job', disabled: false },
  { label: 'Debtor', value: 'debtor', disabled: false },
  { label: 'Start Date', value: 'startDate', disabled: false },
  { label: 'Due Date', value: 'dueDate', disabled: false },
  { label: 'Completed Date', value: 'completedDate', disabled: false },
  { label: 'Turnaround', value: 'turnaround', disabled: false },
  { label: 'Delivery', value: 'delivery', disabled: false },
];

export const VisibleProperties: React.FC<{
  workType: WorkType;
  kanbanOrList: KanbanOrList;
  testId?: string;
}> = ({ workType, kanbanOrList, testId = `visible-properties` }) => {
  const [visibleProperties, setVisibleProperties] = useVisibleProperties();
  return (
    <ConfigMenu
      data-testid={testId}
      trigger={({ triggerProps }) => (
        <Tooltip content="Properties">
          <IconButton
            label="Properties"
            icon={SlidersIcon}
            size="small"
            weight="none"
            tone="passive"
            data-testid={makeId(testId, 'icon-button')}
            {...triggerProps}
          />
        </Tooltip>
      )}
    >
      {workType === 'tasks' && kanbanOrList === 'list' && (
        <ConfigMenuGroup
          title="Tasks List visible properties"
          onChange={(x) =>
            setVisibleProperties((prev: VisiblePropertiesType) => ({
              ...prev,
              taskList: Object.fromEntries(
                taskListOptions.map<
                  [keyof VisiblePropertiesType['taskList'], boolean]
                >(({ value, disabled }) => [
                  value,
                  disabled || x.includes(value),
                ])
              ) as VisiblePropertiesType['taskList'],
            }))
          }
          onReorder={(reorderedOptions) =>
            setVisibleProperties((prev: VisiblePropertiesType) => ({
              ...prev,
              taskList: Object.fromEntries(
                reorderedOptions.map(({ value }) => [
                  value,
                  prev.taskList[value],
                ])
              ) as VisiblePropertiesType['taskList'],
            }))
          }
          options={Object.entries(visibleProperties.taskList).map(
            ([k]) => taskListOptions.find(({ value }) => value === k)!
          )}
          value={(
            Object.entries(visibleProperties.taskList) as [
              keyof VisiblePropertiesType['taskList'],
              boolean,
            ][]
          )
            .filter(([, v]) => v)
            .map(([k]) => k)}
          data-testid={makeId(testId, 'task-list-props')}
        />
      )}
      {workType === 'tasks' && kanbanOrList === 'kanban' && (
        <ConfigMenuGroup
          title="Tasks Kanban visible properties"
          onChange={(x) =>
            setVisibleProperties((prev: VisiblePropertiesType) => ({
              ...prev,
              taskBoard: Object.fromEntries(
                taskBoardOptions.map<
                  [keyof VisiblePropertiesType['taskBoard'], boolean]
                >(({ value, disabled }) => [
                  value,
                  disabled || x.includes(value),
                ])
              ) as VisiblePropertiesType['taskBoard'],
            }))
          }
          options={taskBoardOptions}
          value={(
            Object.entries(visibleProperties.taskBoard) as [
              keyof VisiblePropertiesType['taskBoard'],
              boolean,
            ][]
          )
            .filter(([, v]) => v)
            .map(([k]) => k)}
          data-testid={makeId(testId, 'task-kanban-props')}
        />
      )}
      {workType === 'jobs' && kanbanOrList === 'list' && (
        <ConfigMenuGroup
          title="Jobs List visible properties"
          onChange={(x) =>
            setVisibleProperties((prev: VisiblePropertiesType) => ({
              ...prev,
              jobList: Object.fromEntries(
                jobListOptions.map<
                  [keyof VisiblePropertiesType['jobList'], boolean]
                >(({ value, disabled }) => [
                  value,
                  disabled || x.includes(value),
                ])
              ) as VisiblePropertiesType['jobList'],
            }))
          }
          onReorder={(reorderedOptions) =>
            setVisibleProperties((prev: VisiblePropertiesType) => ({
              ...prev,
              jobList: Object.fromEntries(
                reorderedOptions.map(({ value }) => [
                  value,
                  prev.jobList[value],
                ])
              ) as VisiblePropertiesType['jobList'],
            }))
          }
          options={Object.entries(visibleProperties.jobList).map(
            ([k]) => jobListOptions.find(({ value }) => value === k)!
          )}
          value={(
            Object.entries(visibleProperties.jobList) as [
              keyof VisiblePropertiesType['jobList'],
              boolean,
            ][]
          )
            .filter(([, v]) => v)
            .map(([k]) => k)}
          data-testid={makeId(testId, 'job-list-props')}
        />
      )}
      {workType === 'jobs' && kanbanOrList === 'kanban' && (
        <ConfigMenuGroup
          title="Jobs Kanban visible properties"
          onChange={(x) =>
            setVisibleProperties((prev: VisiblePropertiesType) => ({
              ...prev,
              jobBoard: Object.fromEntries(
                jobBoardOptions.map<
                  [keyof VisiblePropertiesType['jobBoard'], boolean]
                >(({ value, disabled }) => [
                  value,
                  disabled || x.includes(value),
                ])
              ) as VisiblePropertiesType['jobBoard'],
            }))
          }
          options={jobBoardOptions}
          value={(
            Object.entries(visibleProperties.jobBoard) as [
              keyof VisiblePropertiesType['jobBoard'],
              boolean,
            ][]
          )
            .filter(([, v]) => v)
            .map(([k]) => k)}
          data-testid={makeId(testId, 'job-kanban-props')}
        />
      )}
      {workType === 'completedJobs' && (
        <ConfigMenuGroup
          title="Completed Jobs Table visible properties"
          onChange={(x) =>
            setVisibleProperties((prev: VisiblePropertiesType) => ({
              ...prev,
              completedJobTable: Object.fromEntries(
                completedJobsTableOptions.map<
                  [keyof VisiblePropertiesType['completedJobTable'], boolean]
                >(({ value, disabled }) => [
                  value,
                  disabled || x.includes(value),
                ])
              ) as VisiblePropertiesType['completedJobTable'],
            }))
          }
          onReorder={(reorderedOptions) =>
            setVisibleProperties((prev: VisiblePropertiesType) => ({
              ...prev,
              completedJobTable: Object.fromEntries(
                reorderedOptions.map(({ value }) => [
                  value,
                  prev.completedJobTable[value],
                ])
              ) as VisiblePropertiesType['completedJobTable'],
            }))
          }
          options={Object.entries(visibleProperties.completedJobTable).map(
            ([k]) => completedJobsTableOptions.find(({ value }) => value === k)!
          )}
          value={(
            Object.entries(visibleProperties.completedJobTable) as [
              keyof VisiblePropertiesType['completedJobTable'],
              boolean,
            ][]
          )
            .filter(([, v]) => v)
            .map(([k]) => k)}
          data-testid={makeId(testId, 'completed-jobs-table-props')}
        />
      )}
    </ConfigMenu>
  );
};
